import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Plus,
  Trash2,
  ClipboardList,
  Send
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import purchaseRequisitionService from '../../services/purchaseRequisitionService'
import { getUsers } from '../../services/projectService'
import productMasterService from '../../services/productMasterService'
import procurementServiceMasterService from '../../services/procurementServiceMasterService'
import categoryMasterService from '../../services/categoryMasterService'
import supplierMasterService from '../../services/supplierMasterService'
import '../../styles/expenseClaimForm.css'
import '../Accounts/PaymentForm.css'

const PurchaseRequisitionForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = location.pathname.includes('/edit')
  const isView = location.pathname.includes('/view')
  const requisitionData = location.state?.requisitionData

  const [formData, setFormData] = useState({
    requisitionNumber: '',
    requester: '',
    requesterName: '',
    requesterEmail: '',
    requesterDepartment: '',
    requesterDesignation: '',
    requisitionDate: new Date().toISOString().split('T')[0],
    requiredDate: '',
    priority: 'Medium',
    project: '',
    projectName: '',
    department: '',
    costCenter: '',
    requisitionItems: [],
    justification: '',
    notes: ''
  })

  const [isLoadingRequisitionNumber, setIsLoadingRequisitionNumber] = useState(true)
  const [users, setUsers] = useState([])
  const [products, setProducts] = useState([])
  const [services, setServices] = useState([])
  const [categories, setCategories] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(false)

  // Load initial data
  useEffect(() => {
    if (!isEdit && !isView) {
      fetchRequisitionNumber()
    } else if (requisitionData) {
      loadRequisitionData(requisitionData._id)
    }

    // Load dropdown data
    loadDropdownData()
  }, [])

  const fetchRequisitionNumber = async () => {
    try {
      setIsLoadingRequisitionNumber(true)
      const response = await purchaseRequisitionService.getNextRequisitionNumber()
      if (response.success && response.data) {
        setFormData(prev => ({
          ...prev,
          requisitionNumber: response.data.requisitionNumber || response.data.formattedValue || 'PR0001'
        }))
      }
    } catch (error) {
      console.error('Error fetching requisition number:', error)
      setFormData(prev => ({ ...prev, requisitionNumber: 'PR0001' }))
    } finally {
      setIsLoadingRequisitionNumber(false)
    }
  }

  const loadRequisitionData = async (id) => {
    try {
      setLoading(true)
      const response = await purchaseRequisitionService.getPurchaseRequisitionById(id)
      if (response.success && response.data) {
        const req = response.data
        setFormData({
          requisitionNumber: req.requisitionNumber || '',
          requester: req.requester?._id || req.requester || '',
          requesterName: req.requesterName || '',
          requesterEmail: req.requesterEmail || '',
          requesterDepartment: req.requesterDepartment || '',
          requesterDesignation: req.requesterDesignation || '',
          requisitionDate: req.requisitionDate ? new Date(req.requisitionDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          requiredDate: req.requiredDate ? new Date(req.requiredDate).toISOString().split('T')[0] : '',
          priority: req.priority || 'Medium',
          project: req.project?._id || req.project || '',
          projectName: req.projectName || '',
          department: req.department || '',
          costCenter: req.costCenter || '',
          requisitionItems: req.requisitionItems || [],
          justification: req.justification || '',
          notes: req.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading requisition data:', error)
      toast.error('Failed to load requisition data')
    } finally {
      setLoading(false)
      setIsLoadingRequisitionNumber(false)
    }
  }

  const loadDropdownData = async () => {
    try {
      const [usersRes, productsRes, servicesRes, categoriesRes, suppliersRes] = await Promise.all([
        getUsers(),
        productMasterService.getAllProducts({ status: 'Active', limit: 1000 }),
        procurementServiceMasterService.getAllProcurementServices({ status: 'Active', limit: 1000 }),
        categoryMasterService.getAllCategories({ status: 'Active', limit: 1000 }),
        supplierMasterService.getAllSuppliers({ status: 'Active', limit: 1000 })
      ])

      if (usersRes.success) setUsers(usersRes.data || [])
      if (productsRes.success) setProducts(productsRes.data || [])
      if (servicesRes.success) setServices(servicesRes.data || [])
      if (categoriesRes.success) setCategories(categoriesRes.data || [])
      if (suppliersRes.success) setSuppliers(suppliersRes.data || [])
    } catch (error) {
      console.error('Error loading dropdown data:', error)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Update requester details when requester is selected
    if (name === 'requester') {
      const selectedUser = users.find(u => u._id === value)
      if (selectedUser) {
        setFormData(prev => ({
          ...prev,
          requesterName: `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim(),
          requesterEmail: selectedUser.email || '',
          requesterDepartment: selectedUser.department || '',
          requesterDesignation: selectedUser.designation || ''
        }))
      }
    }
  }

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.requisitionItems]
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: field === 'quantity' || field === 'estimatedUnitPrice' || field === 'estimatedTotalPrice' 
        ? parseFloat(value) || 0 
        : value
    }

    // Calculate total price for this item
    const quantity = updatedItems[index].quantity || 0
    const unitPrice = updatedItems[index].estimatedUnitPrice || 0
    updatedItems[index].estimatedTotalPrice = quantity * unitPrice

    // Update names when IDs are selected
    if (field === 'product') {
      const selectedProduct = products.find(p => p._id === value)
      if (selectedProduct) {
        updatedItems[index].productName = selectedProduct.productName || ''
        updatedItems[index].productCode = selectedProduct.productCode || ''
        updatedItems[index].unitOfMeasurement = selectedProduct.unitOfMeasurement || 'Unit'
      }
    }

    if (field === 'service') {
      const selectedService = services.find(s => s._id === value)
      if (selectedService) {
        updatedItems[index].serviceName = selectedService.serviceName || ''
        updatedItems[index].serviceCode = selectedService.serviceCode || ''
        updatedItems[index].unitOfMeasurement = selectedService.unitOfMeasurement || 'Hour'
      }
    }

    if (field === 'category') {
      const selectedCategory = categories.find(c => c._id === value)
      if (selectedCategory) {
        updatedItems[index].categoryName = selectedCategory.categoryName || ''
      }
    }

    if (field === 'preferredSupplier') {
      const selectedSupplier = suppliers.find(s => s._id === value)
      if (selectedSupplier) {
        updatedItems[index].preferredSupplierName = selectedSupplier.supplierName || ''
      }
    }

    setFormData(prev => ({
      ...prev,
      requisitionItems: updatedItems
    }))
  }

  const addRequisitionItem = () => {
    setFormData(prev => ({
      ...prev,
      requisitionItems: [
        ...prev.requisitionItems,
        {
          itemNumber: prev.requisitionItems.length + 1,
          itemType: 'Product',
          product: '',
          productName: '',
          productCode: '',
          service: '',
          serviceName: '',
          serviceCode: '',
          category: '',
          categoryName: '',
          description: '',
          quantity: 1,
          unitOfMeasurement: 'Unit',
          estimatedUnitPrice: 0,
          estimatedTotalPrice: 0,
          currency: 'INR',
          preferredSupplier: '',
          preferredSupplierName: '',
          justification: '',
          requiredDate: ''
        }
      ]
    }))
  }

  const removeRequisitionItem = (index) => {
    setFormData(prev => ({
      ...prev,
      requisitionItems: prev.requisitionItems.filter((_, i) => i !== index).map((item, idx) => ({
        ...item,
        itemNumber: idx + 1
      }))
    }))
  }

  const calculateTotals = () => {
    const totalEstimatedAmount = formData.requisitionItems.reduce((sum, item) => 
      sum + (item.estimatedTotalPrice || 0), 0)
    return totalEstimatedAmount
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.requester || !formData.requiredDate) {
        toast.error('Please fill in all required fields')
        return
      }

      if (!formData.requisitionItems || formData.requisitionItems.length === 0) {
        toast.error('Please add at least one requisition item')
        return
      }

      setLoading(true)

      const submitData = {
        ...formData,
        financialSummary: {
          totalEstimatedAmount: calculateTotals(),
          totalTaxAmount: 0,
          totalAmount: calculateTotals(),
          currency: 'INR'
        }
      }

      if (isEdit && requisitionData?._id) {
        const response = await purchaseRequisitionService.updatePurchaseRequisition(requisitionData._id, submitData)
        if (response.success) {
          toast.success('Purchase requisition updated successfully!')
          navigate('/procurement/purchase-requisition')
        } else {
          toast.error(response.message || 'Failed to update purchase requisition')
        }
      } else {
        const response = await purchaseRequisitionService.createPurchaseRequisition(submitData)
        if (response.success) {
          toast.success('Purchase requisition created successfully!')
          navigate('/procurement/purchase-requisition')
        } else {
          toast.error(response.message || 'Failed to create purchase requisition')
        }
      }
    } catch (error) {
      console.error('Error saving purchase requisition:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save purchase requisition')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitForApproval = async () => {
    if (!requisitionData?._id) {
      // Save first, then submit
      await handleSave()
      return
    }

    try {
      setLoading(true)
      const response = await purchaseRequisitionService.submitForApproval(requisitionData._id)
      if (response.success) {
        toast.success('Purchase requisition submitted for approval!')
        navigate('/procurement/purchase-requisition')
      } else {
        toast.error(response.message || 'Failed to submit purchase requisition')
      }
    } catch (error) {
      console.error('Error submitting purchase requisition:', error)
      if (!error._errorHandled) {
        toast.error('Failed to submit purchase requisition')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    if (isEdit || isView) {
      loadRequisitionData(requisitionData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        requester: '',
        requesterName: '',
        requesterEmail: '',
        requesterDepartment: '',
        requesterDesignation: '',
        requiredDate: '',
        priority: 'Medium',
        project: '',
        projectName: '',
        department: '',
        costCenter: '',
        requisitionItems: [],
        justification: '',
        notes: ''
      }))
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Procurement', path: '/procurement' },
    { label: 'Purchase Requests', path: '/procurement/purchase-requisition' },
    { 
      label: isEdit ? 'Edit Requisition' : isView ? 'View Requisition' : 'New Requisition', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (loading && (isEdit || isView)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading requisition...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEdit ? 'Edit Purchase Requisition' : isView ? 'View Purchase Requisition' : 'New Purchase Requisition'}</h1>
          <p>{isEdit ? 'Update this purchase requisition' : isView ? 'Review this purchase requisition' : 'Create a new purchase requisition'}</p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/procurement/purchase-requisition')}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container pay-entry">
        <div className="sac-form-header">
          <h2>Requisition Information</h2>
        </div>

        <form className="form-content">
          <section className="pay-block">
            <div className="pay-block-head">
              <div>
                <h3>Requisition</h3>
                <p>Who is requesting, when it is needed, and where the cost sits.</p>
              </div>
            </div>
            <div className="pay-grid pay-grid-4">
              <div className="form-group">
                <label htmlFor="requisitionNumber" className="form-label required">
                  Requisition Number
                </label>
                <input
                  type="text"
                  id="requisitionNumber"
                  name="requisitionNumber"
                  value={isLoadingRequisitionNumber ? 'Generating...' : formData.requisitionNumber}
                  readOnly
                  className="form-input pay-readonly"
                />
              </div>

              <div className="form-group">
                <label htmlFor="requester" className="form-label required">
                  Requester
                </label>
                <select
                  id="requester"
                  name="requester"
                  value={formData.requester}
                  onChange={handleInputChange}
                  disabled={isView}
                  className="form-input"
                  required
                >
                  <option value="">Select Requester</option>
                  {users.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.firstName} {user.lastName} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="requisitionDate" className="form-label required">
                  Requisition Date
                </label>
                <input
                  type="date"
                  id="requisitionDate"
                  name="requisitionDate"
                  value={formData.requisitionDate}
                  onChange={handleInputChange}
                  disabled={isView}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="requiredDate" className="form-label required">
                  Required Date
                </label>
                <input
                  type="date"
                  id="requiredDate"
                  name="requiredDate"
                  value={formData.requiredDate}
                  onChange={handleInputChange}
                  disabled={isView}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="pay-grid pay-grid-3 pay-follow">
              <div className="form-group">
                <label htmlFor="priority" className="form-label">
                  Priority
                </label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  disabled={isView}
                  className="form-input"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="department" className="form-label">
                  Department
                </label>
                <input
                  type="text"
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  disabled={isView}
                  className="form-input"
                  placeholder="Department"
                />
              </div>

              <div className="form-group">
                <label htmlFor="costCenter" className="form-label">
                  Cost Center
                </label>
                <input
                  type="text"
                  id="costCenter"
                  name="costCenter"
                  value={formData.costCenter}
                  onChange={handleInputChange}
                  disabled={isView}
                  className="form-input"
                  placeholder="Cost Center"
                />
              </div>
            </div>
          </section>

          <section className="pay-block">
            <div className="pay-block-head">
              <div>
                <h3>Items</h3>
                <p>Products or services to buy, with quantity and an estimated price.</p>
              </div>
              {!isView && (
                <button
                  type="button"
                  onClick={addRequisitionItem}
                  className="btn btn-primary"
                >
                  <Plus size={16} />
                  Add Item
                </button>
              )}
            </div>

            {formData.requisitionItems.map((item, index) => (
              <div key={index} className="expense-item-card-modern">
                <div className="expense-item-header">
                  <div className="expense-item-number">
                    <span className="item-number-badge">{index + 1}</span>
                    <span className="item-number-text">Item {index + 1}</span>
                  </div>
                  {!isView && (
                    <button
                      type="button"
                      onClick={() => removeRequisitionItem(index)}
                      className="btn-icon-danger"
                      title="Remove item"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                <div className="pay-grid pay-grid-4">
                  <div className="form-group">
                    <label className="form-label required">Item Type</label>
                    <select
                      value={item.itemType || 'Product'}
                      onChange={(e) => handleItemChange(index, 'itemType', e.target.value)}
                      className="form-input"
                      disabled={isView}
                    >
                      <option value="Product">Product</option>
                      <option value="Service">Service</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label required">
                      {item.itemType === 'Product' ? 'Product' : 'Service'}
                    </label>
                    <select
                      value={item.itemType === 'Product' ? (item.product || '') : (item.service || '')}
                      onChange={(e) => handleItemChange(index, item.itemType === 'Product' ? 'product' : 'service', e.target.value)}
                      className="form-input"
                      required
                      disabled={isView}
                    >
                      <option value="">Select {item.itemType === 'Product' ? 'Product' : 'Service'}</option>
                      {item.itemType === 'Product'
                        ? products.map(product => (
                            <option key={product._id} value={product._id}>
                              {product.productName} ({product.productCode})
                            </option>
                          ))
                        : services.map(service => (
                            <option key={service._id} value={service._id}>
                              {service.serviceName} ({service.serviceCode})
                            </option>
                          ))
                      }
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select
                      value={item.category || ''}
                      onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                      className="form-input"
                      disabled={isView}
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat._id} value={cat._id}>
                          {cat.categoryName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Preferred Supplier</label>
                    <select
                      value={item.preferredSupplier || ''}
                      onChange={(e) => handleItemChange(index, 'preferredSupplier', e.target.value)}
                      className="form-input"
                      disabled={isView}
                    >
                      <option value="">Select Supplier</option>
                      {suppliers.map(supplier => (
                        <option key={supplier._id} value={supplier._id}>
                          {supplier.supplierName} ({supplier.supplierCode})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="pay-grid pay-grid-4 pay-follow">
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <input
                      type="text"
                      value={item.description || ''}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      className="form-input"
                      placeholder="Item description"
                      disabled={isView}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label required">Quantity</label>
                    <input
                      type="number"
                      value={item.quantity || 0}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      className="form-input"
                      min="0.01"
                      step="0.01"
                      required
                      disabled={isView}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Unit</label>
                    <input
                      type="text"
                      value={item.unitOfMeasurement || ''}
                      readOnly
                      className="form-input pay-readonly"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Unit Price</label>
                    <input
                      type="number"
                      value={item.estimatedUnitPrice || 0}
                      onChange={(e) => handleItemChange(index, 'estimatedUnitPrice', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isView}
                    />
                  </div>
                </div>

                <div className="pay-grid pay-grid-3 pay-follow">
                  <div className="form-group">
                    <label className="form-label">Total Price</label>
                    <div className="expense-item-total-display">
                      {item.currency || 'INR'} {(item.estimatedTotalPrice || 0).toFixed(2)}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Required Date</label>
                    <input
                      type="date"
                      value={item.requiredDate ? new Date(item.requiredDate).toISOString().split('T')[0] : ''}
                      onChange={(e) => handleItemChange(index, 'requiredDate', e.target.value)}
                      className="form-input"
                      disabled={isView}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Justification</label>
                    <input
                      type="text"
                      value={item.justification || ''}
                      onChange={(e) => handleItemChange(index, 'justification', e.target.value)}
                      className="form-input"
                      placeholder="Why is this needed?"
                      disabled={isView}
                    />
                  </div>
                </div>
              </div>
            ))}

            {formData.requisitionItems.length === 0 && (
              <div className="empty-state-card">
                <div className="empty-state-icon">
                  <ClipboardList size={48} />
                </div>
                <h3 className="empty-state-title">No requisition items added</h3>
                <p className="empty-state-description">
                  {isView
                    ? 'This requisition has no line items.'
                    : 'Add a product or service to start this requisition.'}
                </p>
              </div>
            )}
          </section>

          {formData.requisitionItems.length > 0 && (
            <section className="pay-block">
              <div className="pay-block-head">
                <div>
                  <h3>Estimate</h3>
                  <p>Sum of the line totals on this requisition.</p>
                </div>
              </div>
              <div className="pay-inr">
                <span>Total estimated amount</span>
                <strong>INR {calculateTotals().toFixed(2)}</strong>
                <p>{formData.requisitionItems.length} item{formData.requisitionItems.length === 1 ? '' : 's'}</p>
              </div>
            </section>
          )}

          <section className="pay-block">
            <div className="pay-block-head">
              <div>
                <h3>Notes</h3>
                <p>Why this purchase is needed, and anything else the approver should see.</p>
              </div>
            </div>
            <div className="pay-grid pay-grid-2">
              <div className="form-group">
                <label htmlFor="justification" className="form-label">
                  Justification
                </label>
                <textarea
                  id="justification"
                  name="justification"
                  value={formData.justification}
                  onChange={handleInputChange}
                  className="form-textarea"
                  rows="3"
                  placeholder="Provide justification for this requisition"
                  disabled={isView}
                />
              </div>

              <div className="form-group">
                <label htmlFor="notes" className="form-label">
                  Notes
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="form-textarea"
                  rows="3"
                  placeholder="Enter any additional notes"
                  disabled={isView}
                />
              </div>
            </div>
          </section>

          {!isView && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleReset}
                className="btn btn-secondary"
              >
                <RotateCcw size={16} />
                Reset
              </button>
              <div className="pay-footer-actions">
                <button
                  type="button"
                  onClick={handleSave}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  <Save size={16} />
                  {loading ? 'Saving...' : 'Save'}
                </button>
                {isEdit && requisitionData && requisitionData.approvalWorkflow?.status === 'Draft' && (
                  <button
                    type="button"
                    onClick={handleSubmitForApproval}
                    className="btn btn-primary pr-submit"
                    disabled={loading}
                  >
                    <Send size={16} />
                    Submit for Approval
                  </button>
                )}
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default PurchaseRequisitionForm

